import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { fmtMoney, monthLabel, PAYMENT_AR } from '../../utils/money';
import { fmtDate } from '../../utils/dates';

// تصدير PDF عبر طباعة المتصفح (نفس نهج المشروع — لا مكتبة خادم).
// يجمع الغلاف + كل مشروع في فصل يبدأ بصفحة جديدة.
export default function ReportPrint() {
  const { funderId, month } = useParams();
  const { api } = useAuth();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const periods = await api(`/financial/funders/${funderId}/periods`);
        const cur = periods.find((p) => p.report_month === month) || periods[0];
        if (!cur) { setError('لا يوجد تقرير'); return; }
        const cover = await api(`/financial/periods/${cur.id}`);
        const details = await Promise.all(
          (cover.summary.funded_projects || []).map((p) =>
            api(`/financial/periods/${cur.id}/projects/${p.id}`)),
        );
        setDoc({ cover, details });
      } catch (e) { setError(e.message); }
    })();
  }, [funderId, month, api]);

  useEffect(() => {
    if (doc) {
      const t = setTimeout(() => window.print(), 500);
      return () => clearTimeout(t);
    }
  }, [doc]);

  if (error) return <div style={{ padding: 40, fontFamily: 'Tajawal, Arial' }} dir="rtl">{error}</div>;
  if (!doc) return <div style={{ padding: 40, fontFamily: 'Tajawal, Arial' }} dir="rtl">جاري تجهيز التقرير...</div>;

  const { cover, details } = doc;
  const t = cover.summary.totals;

  return (
    <div dir="rtl" className="fr-print">
      <style>{PRINT_CSS}</style>

      <button className="no-print" onClick={() => window.print()} style={btnStyle}>🖨 طباعة / حفظ PDF</button>

      <header className="fr-head">
        <div>
          <div className="kicker">تقرير مالي شهري</div>
          <h1>{cover.funder.name}</h1>
          <div className="sub">
            {monthLabel(cover.period.report_month)}
            {cover.period.letter_no ? ` · ${cover.period.letter_no}` : ''}
            {cover.period.cutoff_date ? ` · حتى ${fmtDate(cover.period.cutoff_date)}` : ''}
          </div>
        </div>
      </header>

      <h2>خطاب الإجمالي — المشاريع المموَّلة</h2>
      <table>
        <thead>
          <tr><th>المشروع</th><th>إجمالي التمويل</th><th>المستهلَك</th><th>المتبقّي</th><th>حالة السداد</th><th>المدفوع</th></tr>
        </thead>
        <tbody>
          {cover.summary.funded_projects.map((p) => (
            <tr key={p.id}>
              <td className="r">{p.name}</td>
              <td>{fmtMoney(p.funding_total)}</td>
              <td>{fmtMoney(p.total_used)}</td>
              <td>{fmtMoney(p.total_remaining)}</td>
              <td>{PAYMENT_AR[p.payment_status]}</td>
              <td>{fmtMoney(p.paid_amount)}</td>
            </tr>
          ))}
          <tr className="total">
            <td>الإجمالي</td><td>{fmtMoney(t.funding_total)}</td><td>{fmtMoney(t.total_used)}</td>
            <td>{fmtMoney(t.total_remaining)}</td><td></td><td>{fmtMoney(t.paid_amount)}</td>
          </tr>
        </tbody>
      </table>

      {cover.summary.pending_projects.length > 0 && (
        <>
          <h3>مبالغ معتمدة بانتظار التمويل</h3>
          <table>
            <thead><tr><th>المشروع</th><th>المبلغ المعتمد</th></tr></thead>
            <tbody>
              {cover.summary.pending_projects.map((p) => (
                <tr key={p.id}><td className="r">{p.name}</td><td>{fmtMoney(p.approved_amount)}</td></tr>
              ))}
              <tr className="total"><td>الإجمالي</td><td>{fmtMoney(t.pending_total)}</td></tr>
            </tbody>
          </table>
        </>
      )}

      {details.map((d) => <ProjectChapter key={d.project.id} detail={d} />)}

      <footer className="fr-foot">
        {cover.funder.name}{cover.funder.contact_phone ? ` · ${cover.funder.contact_phone}` : ''}
      </footer>
    </div>
  );
}

function ProjectChapter({ detail }) {
  const p = detail.project;
  return (
    <section className="chapter">
      <h2>{p.name}</h2>
      <div className="meta">
        وحدة القياس: {p.unit_basis || '—'} · التمويل {fmtMoney(p.funding_total)} · المستهلَك {fmtMoney(p.total_used)} ·
        المتبقّي {fmtMoney(p.total_remaining)} · {PAYMENT_AR[p.payment_status]}
      </div>

      <h3>ملخّص البنود</h3>
      <table>
        <thead><tr><th>البند</th><th>المنفّذ</th><th>السقف</th><th>المستهلَك</th><th>المتبقّي</th></tr></thead>
        <tbody>
          {p.summary_items.map((it, i) => (
            <tr key={i}><td className="r">{it.name_ar}</td><td>{it.executed_label || '—'}</td><td>{fmtMoney(it.ceiling_amount)}</td><td>{fmtMoney(it.used_amount)}</td><td>{fmtMoney(it.remaining_amount)}</td></tr>
          ))}
        </tbody>
      </table>

      {p.transfers.length > 0 && (
        <>
          <h3>التحويلات</h3>
          <table>
            <thead><tr><th>من</th><th>إلى</th><th>المبلغ</th><th>المرجع</th></tr></thead>
            <tbody>
              {p.transfers.map((tr) => (
                <tr key={tr.id}><td className="r">{tr.from_title || '—'}</td><td>{tr.to_title || 'سداد/سحب'}</td><td>{fmtMoney(tr.amount)}</td><td className="r">{tr.note || tr.reference || '—'}</td></tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <h3>العقود</h3>
      {p.contracts.map((c) => (
        <div key={c.id} className="contract">
          <div className="ctitle">
            {c.title}{c.contract_no ? ` — العقد ${c.contract_no}` : ''} ({c.status === 'active' ? 'ساري' : c.status === 'ended' ? 'منتهٍ' : '—'})
          </div>
          <table>
            <thead><tr><th>البند</th><th>المنفّذ</th><th>السعر</th><th>السقف</th><th>المستهلَك</th><th>المتبقّي</th></tr></thead>
            <tbody>
              {c.items.map((it) => (
                <tr key={it.id}><td className="r">{it.name_ar}</td><td>{it.executed_label || '—'}</td><td>{it.unit_price != null ? fmtMoney(it.unit_price) : '—'}</td><td>{it.ceiling_amount != null ? fmtMoney(it.ceiling_amount) : '—'}</td><td>{fmtMoney(it.used_amount)}</td><td>{fmtMoney(it.remaining_amount)}</td></tr>
              ))}
            </tbody>
          </table>
          <div className="cfoot">
            المستنفَذ {fmtMoney(c.total_used)}
            {c.carried_in > 0 ? ` · مرحَّل إليه +${fmtMoney(c.carried_in)}` : ''}
            {c.carried_out > 0 ? ` · محوَّل منه −${fmtMoney(c.carried_out)}` : ''}
            {' · '}{c.total_remaining < 0 ? 'عجز ' : 'متبقّي '}{fmtMoney(Math.abs(c.total_remaining))} · {PAYMENT_AR[c.payment_status]}
          </div>
        </div>
      ))}
    </section>
  );
}

const btnStyle = { position: 'fixed', top: 10, insetInlineStart: 10, padding: '8px 14px', background: 'var(--c-navy)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', zIndex: 10 };

const PRINT_CSS = `
.fr-print { font-family: 'Tajawal', Arial, sans-serif; color: var(--text); padding: 24px; max-width: 900px; margin: 0 auto; }
.fr-print h1 { font-size: 22px; font-weight: 900; color: var(--text-strong); margin: 0; }
.fr-print h2 { font-size: 17px; font-weight: 700; color: var(--text-strong); margin: 18px 0 10px; }
.fr-print h3 { font-size: 14px; color: var(--text-body); margin: 14px 0 6px; }
.fr-head { border-bottom: 1px solid var(--border); padding-bottom: 10px; margin-bottom: 16px; }
.fr-head .kicker { font-size: 11px; letter-spacing: .04em; color: var(--text-muted); }
.fr-head .sub { font-size: 13px; color: var(--text-muted); margin-top: 4px; }
.fr-print table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 8px; }
.fr-print th, .fr-print td { border: 1px solid var(--border); padding: 6px 8px; text-align: center; }
.fr-print td.r, .fr-print th { text-align: right; }
.fr-print th { background: var(--c-navy); color: #fff; }
.fr-print tr.total td { background: var(--surface-alt); font-weight: 700; }
.fr-print .meta { font-size: 12px; color: var(--text-body); margin-bottom: 8px; }
.fr-print .contract { margin-bottom: 10px; }
.fr-print .ctitle { font-weight: 700; font-size: 13px; margin: 8px 0 4px; }
.fr-print .cfoot { font-size: 12px; color: var(--text-body); padding: 4px 0; }
.fr-print .chapter { page-break-before: always; }
.fr-foot { margin-top: 20px; padding-top: 8px; border-top: 1px solid var(--border); font-size: 11px; color: var(--text-faint); text-align: center; }
@page { size: A4; margin: 15mm; }
@media print { .no-print { display: none; } .fr-print { padding: 0; } .fr-print th { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
`;
